<script setup>
import { reactive, ref, computed, defineAsyncComponent } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import AuthService from '../../services/AuthService';
import SnackbarComposables from '../../composables/SnackbarComposables';
import ZdhcServiceV2 from '../../services/Zdhc/ZdhcServiceV2';
import ZdhcServiceV5 from '../../services/Zdhc/ZdhcServiceV5';
import ProfileService from '../../services/ProfileService';
import moment from 'moment';
/* Components */
const RegistrationTotarialModal = defineAsyncComponent( () => import('../../components/modals/RegistrationTutorial.vue'));
/* Data */
const router = useRouter();
const store = useStore();
const loginStatus = ref('void');
const showTutorialModal = ref(false);
const incheckError = ref(false);
const credentials_error = ref(false);
const { t } = useI18n();
const fields = reactive({
    email: '',
    password: ''
});
const { login } = AuthService();
const {addSnackbar} = SnackbarComposables();
const {updateSuscriptionExpiration} = ProfileService();
const { userOrganizations, InCheck, getSubscriptionStatus,} = ZdhcServiceV2();
const { inCheckAccessToken, inCheckSubscriptionStatus} = ZdhcServiceV5();
const supportEmail = process.env.VUE_APP_YMPACT_SUPPORT_EMAIL;
/* Computed */
const setSubmitContent = computed( () => 
{
    switch(loginStatus.value)
    {
        case 'void':
            return t('auth.signin.signinButton', store.state.locale);
        case 'loading': case 'success':
            return `<svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>`;
        case 'error':
            return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-5 w-5 fill-current text-white"><path d="M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z"/></svg>`;
        default:
            return t('auth.signin.signinButton', store.state.locale);
    }
});
const setSubmitClasses = computed( () =>
{
    switch(loginStatus.value)
    {
        case 'void': case 'loading': default:   case 'success':
            return 'bg-blue-500 hover:bg-blue-300';
        case 'error':
            return `bg-red-500 hover:bg-red-700`;
    }
});
/* Methods */
const signin = () => 
{   
    loginStatus.value = 'loading';
    setTimeout( () => {if(loginStatus.value != 'void') loginStatus.value = 'void'}, 10000);
    credentials_error.value = false;
    store.commit('errors/CLEAR_STATE');
    login(fields)
    .then( res => 
    {
        
        //if(res === 200) return router.push(`/${store.state.locale}/dashboard`);
        if(res === 200) return getAccessToken();
        else
        {
            credentials_error.value = true;
            loginStatus.value = 'error';
        }
    });
};
const getAccessToken = async () =>
{
    if(store.state.auth.user.user_key)
    {
        try 
        {
            let res;
            if(process.env.VUE_APP_ZDHC_VERSION == 2) res = await InCheck(store.state.auth.user.user_key);
            else res = await inCheckAccessToken(store.state.auth.user.user_key);
            if(res && res.status === 'success') 
            {
                const response = await userOrganizations()
                if(response)
                {
                    store.commit('auth/SET_ORG_GUID', response.Organizations[0].OrgGUID);
                    store.commit('auth/SET_SUPPLIER_AID', response.Organizations[0].SupplierAID);
                    /**
                     *
                     * If user has a valid subscription_expires_at redirect to /dashboard
                     * else check for subscription status
                     *
                     */
                    if(store.state.auth.user.subscription_expires_at &&
                        moment(store.state.auth.user.subscription_expires_at).isAfter(moment()))
                    {
                        return router.push(`/${store.state.locale}/dashboard`)
                    }
                    checkUserIncheckSubscriptionStatus();
                }
            }
            else
            {
                loginStatus.value = 'error';
                router.push({name: 'AccessTokenError'});
            }
        }
        catch(e)
        {
            loginStatus.value = 'error';
            return addSnackbar('error', t('snackbar.refresh', store.state.locale)); // Add a more explicable message + logout
        }
    }
    else return router.push(`/${store.state.locale}/waiting-page`);
};
const checkUserIncheckSubscriptionStatus = async () => 
{
    try 
    {
        let res;
        if(process.env.VUE_APP_ZDHC_VERSION == 2) res = await getSubscriptionStatus(store.state.auth.orgGUID);
        else res = await inCheckSubscriptionStatus(store.state.auth.orgGUID)
        if(res)
        {
            if(res.status === 'ok')
            {
                if(res.expirationDate)
                {
                    updateSuscriptionExpiration({subscription_expires_at: res.expirationDate});
                }
                loginStatus.value = 'success';
                return router.push(`/${store.state.locale}/dashboard`)
            }
            else 
            {
                loginStatus.value = 'error';
                incheckError.value = true;
            }
        }
    }
    catch(e)
    {
        loginStatus.value = 'success';
        return router.push(`/${store.state.locale}/waiting-page`);
    }
};
</script>

<template>
    <div class="relative min-h-screen flex items-center justify-center lg:justify-start">
        <img src="https://source.unsplash.com/gOdavfpH-3s/1260x750" class="absolute inset-0 object-cover w-full h-full" alt="" />
        <div class="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-start"></div>
        <div class="relative flex items-center justify-center lg:justify-start h-full w-full"> 
            <div class="lg:w-1/2 p-5">   
                <!-- Form -->
                <div class="w-full max-w-xl xl:px-8">
                    <div class="bg-white rounded shadow-2xl p-7 sm:p-10">
                        <div class="flex flex-col lg:flex-row items-center justify-between space-y-3 lg:space-y-0 lg:space-x-10 mb-4 lg:mb-7">
                            <h3 class="text-2xl text-center order-2 lg:order-1">
                                {{ $t('auth.signin.title', $store.state.locale) }}
                            </h3>
                            <router-link :to="`/${$store.state.locale}/`" title="Home">
                                <!--<svg viewBox="0 0 119 30" xmlns="http://www.w3.org/2000/svg" class="mx-auto w-32 fill-current"> <path d="M2.94098 27.825H28.591V2.175H2.94098V27.825ZM23.654 12.532H18.234V7.112H23.654V12.532ZM7.87698 7.112H13.3V17.468H23.654V22.888H7.87698V7.112ZM45.294 2.175C42.7574 2.175 40.2778 2.92718 38.1688 4.33641C36.0597 5.74564 34.4159 7.74863 33.4452 10.0921C32.4745 12.4356 32.2206 15.0142 32.7154 17.502C33.2103 19.9898 34.4317 22.275 36.2253 24.0686C38.0189 25.8623 40.3041 27.0837 42.7919 27.5786C45.2798 28.0734 47.8584 27.8195 50.2019 26.8488C52.5454 25.8781 54.5483 24.2343 55.9576 22.1252C57.3668 20.0161 58.119 17.5365 58.119 15C58.115 11.5998 56.7625 8.34004 54.3582 5.93575C51.9539 3.53145 48.6942 2.17897 45.294 2.175V2.175ZM45.294 22.888C43.7339 22.888 42.2088 22.4254 40.9116 21.5586C39.6145 20.6919 38.6034 19.46 38.0064 18.0186C37.4094 16.5773 37.2532 14.9913 37.5575 13.4611C37.8619 11.931 38.6132 10.5255 39.7163 9.42234C40.8195 8.31919 42.225 7.56793 43.7551 7.26357C45.2852 6.95921 46.8712 7.11542 48.3126 7.71244C49.7539 8.30946 50.9859 9.32049 51.8526 10.6177C52.7194 11.9148 53.182 13.4399 53.182 15C53.1788 17.0911 52.3467 19.0956 50.8681 20.5742C49.3895 22.0528 47.385 22.8848 45.294 22.888V22.888ZM104.013 2.175C101.476 2.17481 98.9967 2.92681 96.8875 4.33592C94.7783 5.74503 93.1344 7.74795 92.1635 10.0914C91.1927 12.4348 90.9385 15.0136 91.4333 17.5014C91.928 19.9893 93.1494 22.2746 94.943 24.0683C96.7365 25.862 99.0217 27.0836 101.51 27.5785C103.997 28.0734 106.576 27.8195 108.92 26.8489C111.263 25.8782 113.266 24.2344 114.675 22.1253C116.085 20.0162 116.837 17.5366 116.837 15C116.833 11.6 115.481 8.34036 113.077 5.9361C110.673 3.53183 107.413 2.17924 104.013 2.175V2.175ZM104.013 22.888C102.453 22.8882 100.928 22.4257 99.6304 21.5591C98.3331 20.6925 97.3219 19.4606 96.7247 18.0193C96.1275 16.578 95.9712 14.9919 96.2754 13.4617C96.5797 11.9316 97.3309 10.526 98.434 9.4227C99.5371 8.31944 100.943 7.56808 102.473 7.26364C104.003 6.9592 105.589 7.11535 107.03 7.71235C108.472 8.30934 109.704 9.32036 110.571 10.6176C111.437 11.9148 111.9 13.4399 111.9 15C111.897 17.0909 111.065 19.0952 109.586 20.5738C108.108 22.0524 106.104 22.8846 104.013 22.888V22.888ZM74.653 2.175C72.1164 2.175 69.6368 2.92718 67.5278 4.33641C65.4187 5.74564 63.7749 7.74863 62.8042 10.0921C61.8335 12.4356 61.5796 15.0142 62.0744 17.502C62.5693 19.9898 63.7907 22.275 65.5843 24.0686C67.3779 25.8623 69.6631 27.0837 72.1509 27.5786C74.6387 28.0734 77.2174 27.8195 79.5609 26.8488C81.9044 25.8781 83.9073 24.2343 85.3166 22.1252C86.7258 20.0161 87.478 17.5365 87.478 15C87.474 11.5998 86.1215 8.34004 83.7172 5.93575C81.3129 3.53145 78.0532 2.17897 74.653 2.175ZM74.653 22.888C73.1994 22.8887 71.774 22.4878 70.5339 21.7295C69.2939 20.9712 68.2874 19.8851 67.6257 18.5909C66.9641 17.2967 66.6728 15.8448 66.7842 14.3956C66.8956 12.9463 67.4052 11.556 68.2569 10.3781C69.1086 9.20026 70.2692 8.28061 71.6105 7.72071C72.9519 7.1608 74.4219 6.98242 75.8582 7.20524C77.2946 7.42807 78.6414 8.04343 79.7501 8.98342C80.8588 9.9234 81.6862 11.1515 82.141 12.532H74.653V17.468H82.141C81.6201 19.0433 80.6165 20.4147 79.2724 21.3875C77.9284 22.3604 76.3122 22.8853 74.653 22.888V22.888Z"></path> </svg>-->
                                <img src="@/assets/img/logo/logo-ympact.png" class="w-40">
                            </router-link>
                        </div>
                        <span
                            v-if="credentials_error"
                            class="block text-sm text-red-500 mb-5 text-center lg:text-left" >
                            {{ $t('auth.signin.credentialsError', $store.state.locale) }}
                        </span>

                        <form 
                            method="POST" @submit.prevent="signin"
                            class="mb-3 flex flex-col space-y-3">
                            <div class="mb-1 sm:mb-2">
                                <label for="email" class="inline-block mb-1 uppercase">Email</label>
                                <input id="email" v-model="fields.email" name="email" placeholder="Email" type="email" class="flex-grow w-full h-12 px-4 mb-2 transition duration-200 bg-white border rounded shadow-sm appearance-none focus:shadow-outline border-gray-300 focus:border-blue-500"/>
                            </div>
                            <div class="mb-1 sm:mb-2">
                                <label for="password" class="inline-block mb-1 uppercase">Password</label>
                                <input id="password" v-model="fields.password" name="password" placeholder="Password" required type="password" class="flex-grow w-full h-12 px-4 mb-2 transition duration-200 bg-white border rounded shadow-sm appearance-none focus:shadow-outline border-gray-300 focus:border-blue-500"/>
                            </div>
                            <div class="mt-4 mb-2 sm:mb-4 flex items-center justify-end">
                                <button 
                                    type="submit" 
                                    class="inline-flex items-center justify-center w-full h-12 px-6 font-medium tracking-wide text-white transition duration-300 rounded shadow-md focus:shadow-outline focus:outline-none"
                                    :class="setSubmitClasses"
                                    :title="$t('auth.signin.signinButton', $store.state.locale)" 
                                    v-html="setSubmitContent"
                                    :disabled="loginStatus!=='void'">
                                </button>
                            </div>
                        </form>
                        <div class="flex flex-col space-y-3">
                            <p class="text-xs text-gray-600 sm:text-sm text-center mb-5">
                                {{ $t('auth.signin.msg', $store.state.locale) }} 
                                <router-link
                                    :to="`/${$store.state.locale}/signup`"
                                    :title="$t('auth.signin.register', $store.state.locale)"
                                    class="text-light-green-500 hover:underline"
                                >
                                    {{ $t('auth.signin.register', $store.state.locale) }}
                                </router-link>
                            </p>
                            <p class="text-xs text-gray-600 sm:text-sm text-center">
                                <router-link
                                    :to="`/${$store.state.locale}/reset-password`"
                                    :title="$t('auth.signin.register', $store.state.locale)"
                                    class="text-light-green-500 hover:underline"
                                >
                                    {{ $t('auth.signin.password', $store.state.locale) }}
                                </router-link>
                            </p>
                            <div class="w-full flex items-center justify-center text-lg">
                                <button
                                    class="text-center text-light-green-500 hover:underline"
                                    @click.prevent="showTutorialModal = true">
                                    {{ $t('auth.signup.registrationTutorial', $store.state.locale) }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- -->
        <div v-if="incheckError" class="fixed inset-0 p-6 bg-gray-1000 bg-opacity-90 z-30 overscroll-y-contain overflow-y-auto text-blue-500">
            <div class="flex flex-col w-full h-full items-center justify-center">
                <div class="bg-white dark:bg-gray-900 dark:text-gray-400 border dark:border-gray-700 rounded p-5 flex flex-col space-y-2 w-full md:w-2/3 lg:w-1/2 2xl:w-1/2 max-h-xl overflow-y-auto">
                    <div class="flex items-start justify-between">
                        <button 
                            title="Chiudi"
                            @click.prevent="incheckError = false"
                            class="ml-auto bg-transparent min-w-min flex items-center justify-center transform hover:rotate-90 duration-300">
                                <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 64 64" class="w-4 fill-current text-gray-800 dark:text-gray-400">
                                    <path d="M34.5,32L62.2,4.2c0.7-0.7,0.7-1.8,0-2.5c-0.7-0.7-1.8-0.7-2.5,0L32,29.5L4.2,1.8c-0.7-0.7-1.8-0.7-2.5,0
                                        c-0.7,0.7-0.7,1.8,0,2.5L29.5,32L1.8,59.8c-0.7,0.7-0.7,1.8,0,2.5c0.3,0.3,0.8,0.5,1.2,0.5s0.9-0.2,1.2-0.5L32,34.5l27.7,27.8
                                        c0.3,0.3,0.8,0.5,1.2,0.5c0.4,0,0.9-0.2,1.2-0.5c0.7-0.7,0.7-1.8,0-2.5L34.5,32z"/>
                                </svg>
                        </button>
                    </div>
                    <h1 class="text-xl flex items-center space-x-1 px-3 text-red-500 text-center">
                        <span>{{ $t('signin.errorModal.title', $store.state.locale) }}</span> 
                    </h1>
                    <a 
                        class="w-full text-left p-2 group duration-300 flex items-center justify-center"
                        :href="`mailto:${supportEmail}`">
                        <h2 class="font-semibold group-hover:underline">
                            <span>{{ $t('signin.errorModal.customer_support', $store.state.locale) }}</span> 
                        </h2>
                    </a>
                </div>
            </div>
        </div>
        <teleport to="body">
            <RegistrationTotarialModal 
                v-if="showTutorialModal"
                @closeModal="showTutorialModal = false"
            />
        </teleport>
    </div>
</template>
